// Slide-in detail drawer for a single user or sim. Reads `item.logs` for
// the inline activity log and exposes a "see full page" affordance that
// drills the shell into UserDetailPage / SimDetailPage.
//
// Postgres mapping: drawer is a lightweight summary; consumes the same
// `users` / `simulations` rows as the full detail pages.
// Integration hook: reuse `useUserDetailQuery` / `useSimDetailQuery`
// with a `{ summary: true }` flag for a lighter payload.
import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Box, Button } from '@mui/material';

// The drawer's "teal" button: transparent until hover, then inverts to the
// foreground colour. Matches the outline buttons on the pages behind it.
const TEAL_BUTTON_SX = {
    mt: 1.75,
    width: '100%',
    justifyContent: 'center',
    gap: 1,
    px: 1.5,
    py: 0.75,
    borderRadius: '6px',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'none',
    fontFamily: 'Inter, sans-serif',
    color: 'var(--text)',
    border: '1px solid var(--border2)',
    backgroundColor: 'transparent',
    transition: 'all 150ms',
    '&:hover': { backgroundColor: 'var(--text)', color: 'var(--bg)', borderColor: 'var(--text)' },
    '&:active': { transform: 'scale(0.97)' },
};

export default function Drawer({ item, type, onClose, go }) {
    return (
        <AnimatePresence>
            {item && (
                <motion.div
                    style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'flex-end', zIndex: 1300 }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
                >
                    <motion.div
                        style={{
                            backgroundColor: 'var(--surface)',
                            width: 400,
                            maxWidth: '90vw',
                            height: '100%',
                            borderLeft: '1px solid var(--border)',
                            padding: 16,
                            overflowY: 'auto',
                        }}
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    >
                        {(() => {
                            const sub =
                                type === 'sim'
                                    ? `${item.click} - ${item.diff === 3 ? 'Hard' : item.diff === 2 ? 'Medium' : 'Easy'}`
                                    : `${item.dept} - risk ${item.risk}`;
                            return (
                                <>
                                    <Box
                                        component="span"
                                        sx={{
                                            float: 'right',
                                            fontSize: 13,
                                            cursor: 'pointer',
                                            color: 'var(--muted)',
                                            transition: 'color 150ms',
                                            '&:hover': { color: 'var(--text)' },
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        aria-label="Close"
                                        onClick={onClose}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                onClose();
                                            }
                                        }}
                                    >
                                        &#10005;
                                    </Box>
                                    <Box sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', mb: 0.25, pr: 2.5 }}>{item.name}</Box>
                                    <Box sx={{ fontSize: 11, color: 'var(--muted)', mb: 1.5 }}>{sub}</Box>
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'block',
                                            fontSize: 10,
                                            fontWeight: 600,
                                            letterSpacing: '.07em',
                                            textTransform: 'uppercase',
                                            color: 'var(--dim)',
                                            mb: 0.75,
                                        }}
                                    >
                                        Raw event log
                                    </Box>
                                    {item.logs.map((l, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                fontSize: 11,
                                                fontFamily: 'monospace',
                                                color: 'var(--muted)',
                                                py: 0.75,
                                                borderBottom: '1px solid var(--border)',
                                                display: 'flex',
                                                gap: 1.25,
                                                '&:last-of-type': { borderBottom: 0 },
                                            }}
                                        >
                                            <Box component="span" sx={{ color: 'var(--text)', fontWeight: 600, minWidth: 120, flexShrink: 0 }}>{l.k}</Box>
                                            <span>{l.v}</span>
                                        </Box>
                                    ))}
                                    <Button
                                        sx={TEAL_BUTTON_SX}
                                        onClick={() => { onClose(); go('export-builder', 'Export builder'); }}
                                    >
                                        &#11015; Export this data
                                    </Button>
                                </>
                            );
                        })()}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
